import { type SingleValue, type MultiValue } from 'react-select';

import { type OptionType } from '@hivebrite/design-system/Inputs';

interface QueryFnParams {
  ids?: Array<number>;
  searchText?: string;
}

/**
 * Resolves options from IDs by checking cache and fetching missing ones
 */
export const resolveOptionsFromIds = async <
  TQueryFnData,
  TOptionType extends OptionType,
>(
  ids: Array<number>,
  allAvailableOptions: Array<TOptionType>,
  queryFn: (
    params: QueryFnParams,
    signal: AbortSignal,
  ) => Promise<TQueryFnData>,
  responseToOptions: (response: TQueryFnData) => Array<TOptionType>,
  addOptionsToCache: (options: Array<TOptionType>) => void,
): Promise<MultiValue<TOptionType>> => {
  const idsToFetch: Array<number> = [];
  const optionsFromCache = ids
    .map((id) => {
      const option = allAvailableOptions.find(({ value }) => value === id);
      if (!option) {
        idsToFetch.push(id);
      }
      return option;
    })
    .filter(Boolean) as Array<TOptionType>;

  // All IDs found in cache
  if (idsToFetch.length === 0) {
    return optionsFromCache;
  }

  const abortController = new AbortController();
  const response = await queryFn({ ids: idsToFetch }, abortController.signal);
  const fetchedOptions = responseToOptions(response);

  addOptionsToCache(fetchedOptions);

  return [...optionsFromCache, ...fetchedOptions];
};

/**
 * Resolves a single option from an ID by checking cache and fetching if missing
 */
export const resolveOptionFromId = async <
  TQueryFnData,
  TOptionType extends OptionType,
>(
  id: number,
  allAvailableOptions: Array<TOptionType>,
  queryFn: (
    params: QueryFnParams,
    signal: AbortSignal,
  ) => Promise<TQueryFnData>,
  responseToOptions: (response: TQueryFnData) => Array<TOptionType>,
  addOptionsToCache: (options: Array<TOptionType>) => void,
): Promise<SingleValue<TOptionType>> => {
  // Check cache first
  const optionFromCache = allAvailableOptions.find(({ value }) => value === id);

  if (optionFromCache) {
    return optionFromCache;
  }

  // Fetch from API
  const abortController = new AbortController();
  const response = await queryFn({ ids: [id] }, abortController.signal);
  const fetchedOptions = responseToOptions(response);

  addOptionsToCache(fetchedOptions);

  // Return the first option or null if not found
  return fetchedOptions[0] ?? null;
};
